import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Check, Gift } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime, inr, ORDER_STEPS } from "@/lib/format";
import { Button } from "@/components/ui/button";

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(false);

  const load = () => api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => setError(true));

  useEffect(() => {
    if (user === null) { navigate("/login?next=/account"); return; }
    if (user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user]);

  if (error) return <div className="container-x py-20 text-center" data-testid="order-not-found">Order not found.</div>;
  if (!order) return <div className="container-x py-20 text-center text-stone-400 font-mono text-sm">LOADING…</div>;

  const currentStep = ORDER_STEPS.indexOf(order.status);
  const cancelled = order.status === "cancelled";
  const tracking = order.tracking;
  const ship = order.shiprocket || {};

  const cancel = async () => {
    try {
      await api.post(`/orders/${order.id}/cancel`);
      toast.success("Order cancelled");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="container-x py-10 max-w-4xl mx-auto" data-testid="order-detail-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="eyebrow">ORDER</p>
          <h1 className="font-heading text-3xl font-semibold tracking-tight" data-testid="order-number">{order.order_number}</h1>
          <p className="text-sm text-stone-500 mt-1">{fmtDateTime(order.created_at)}</p>
        </div>
        <div className="flex items-center gap-2">
          {order.has_personalised && (
            <span className="text-[11px] font-mono uppercase tracking-wider px-3 py-1.5 bg-brand-light text-brand border border-brand/30" data-testid="personalised-badge">Personalised · non-returnable</span>
          )}
          <span className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 bg-stone-900 text-white" data-testid="order-status-badge">{order.status}</span>
          {order.status === "placed" && !order.has_personalised && (
            <Button variant="outline" className="text-red-600" onClick={cancel} data-testid="cancel-order-btn">Cancel Order</Button>
          )}
        </div>
      </div>

      {order.gift?.wrap && (
        <div className="mt-4 border border-brand/30 bg-brand-light/50 px-4 py-3 flex items-center gap-2 text-sm" data-testid="gift-indicator">
          <Gift size={16} className="text-brand" /> Gift order{order.gift.hide_price ? " · prices hidden on invoice" : ""}{order.gift.message ? ` · “${order.gift.message}”` : ""}
        </div>
      )}

      {!cancelled && (
        <div className="mt-8 border border-stone-200 bg-white p-6" data-testid="order-timeline">
          <div className="flex items-center">
            {ORDER_STEPS.map((step, i) => {
              const done = currentStep >= i;
              const entry = (order.timeline || []).find((t) => t.status === step);
              return (
                <div key={step} className="flex-1 flex items-center last:flex-none">
                  <div className="flex flex-col items-center text-center">
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center border-2 ${done ? "bg-stone-900 border-stone-900 text-white" : "border-stone-300 text-stone-300"}`}>
                      {done ? <Check size={15} /> : <span className="text-xs font-mono">{i + 1}</span>}
                    </div>
                    <p className={`text-[11px] font-mono uppercase tracking-wider mt-2 ${done ? "text-stone-900" : "text-stone-400"}`}>{step}</p>
                    {entry && <p className="text-[10px] text-stone-400 mt-0.5 hidden sm:block">{fmtDateTime(entry.at)}</p>}
                  </div>
                  {i < ORDER_STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${currentStep > i ? "bg-stone-900" : "bg-stone-200"}`} />}
                </div>
              );
            })}
          </div>
          {ship.awb && (
            <div className="mt-6 border-t border-stone-200 pt-4 flex flex-wrap items-center gap-6" data-testid="tracking-block">
              <div>
                <p className="text-xs font-mono text-stone-500">TRACKING (AWB)</p>
                <p className="font-mono font-semibold" data-testid="tracking-awb">{ship.awb}</p>
              </div>
              <div>
                <p className="text-xs font-mono text-stone-500">COURIER</p>
                <p className="font-medium">{ship.courier}</p>
              </div>
              {tracking && (
                <div>
                  <p className="text-xs font-mono text-stone-500">STATUS</p>
                  <p className="font-medium text-emerald-700" data-testid="tracking-status">{tracking.status}</p>
                </div>
              )}
            </div>
          )}
          {tracking?.activities?.length > 0 && (
            <ul className="mt-4 space-y-2">
              {tracking.activities.map((a, i) => (
                <li key={i} className="text-sm text-stone-600 flex gap-2">
                  <span className="h-2 w-2 rounded-full bg-brand mt-1.5 shrink-0" />
                  {a.status}{a.location ? ` — ${a.location}` : ""}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        <div className="border border-stone-200 bg-white p-6">
          <h2 className="font-heading font-semibold">Pieces</h2>
          <ul className="mt-4 space-y-4">
            {order.items.map((i) => (
              <li key={i.variant_id + (i.personalisation?.summary || "")} className="flex gap-3 text-sm">
                <img src={i.image} alt="" className="h-14 w-14 object-cover border border-stone-200 bg-stone-50" />
                <div className="flex-1">
                  <p className="font-medium leading-tight">{i.title}</p>
                  <p className="text-xs text-stone-500 font-mono">{i.variant_label} × {i.qty}</p>
                  {i.purity && (
                    <p className="text-[11px] font-mono text-stone-400 mt-0.5">
                      {i.purity} {i.metal} · {i.net_weight}g · {inr(i.rate_per_gram)}/g{i.hallmark ? ` · ${i.hallmark}` : ""}
                    </p>
                  )}
                  {i.personalisation?.summary && (
                    <p className="text-xs text-brand mt-1" data-testid={`order-pers-${i.sku}`}>✦ {i.personalisation.summary}</p>
                  )}
                </div>
                <span className="font-medium">{inr(i.price * i.qty)}</span>
              </li>
            ))}
          </ul>
          <dl className="mt-4 border-t border-stone-200 pt-4 space-y-1.5 text-sm">
            <div className="flex justify-between"><dt className="text-stone-500">Subtotal</dt><dd>{inr(order.subtotal)}</dd></div>
            {order.discount > 0 && <div className="flex justify-between text-emerald-700"><dt>Discount {order.coupon_code && `(${order.coupon_code})`}</dt><dd>−{inr(order.discount)}</dd></div>}
            <div className="flex justify-between"><dt className="text-stone-500">Shipping ({order.shipping_label})</dt><dd>{order.shipping_fee === 0 ? "FREE" : inr(order.shipping_fee)}</dd></div>
            {order.gift_wrap_fee > 0 && <div className="flex justify-between"><dt className="text-stone-500">Gift wrap</dt><dd>{inr(order.gift_wrap_fee)}</dd></div>}
            {order.cod_fee > 0 && <div className="flex justify-between"><dt className="text-stone-500">COD fee</dt><dd>{inr(order.cod_fee)}</dd></div>}
            <div className="flex justify-between text-xs text-stone-500"><dt>Incl. GST @ 3% (HSN 7113)</dt><dd>{inr(order.gst_amount)}</dd></div>
            <div className="flex justify-between font-heading font-semibold text-base border-t border-stone-200 pt-2 mt-2"><dt>Total</dt><dd data-testid="order-total">{inr(order.total)}</dd></div>
          </dl>
        </div>

        <div className="space-y-6">
          <div className="border border-stone-200 bg-white p-6">
            <h2 className="font-heading font-semibold">Delivery Address</h2>
            <p className="text-sm mt-3 font-medium">{order.address.name}</p>
            <p className="text-sm text-stone-600">{order.address.line1}{order.address.line2 ? `, ${order.address.line2}` : ""}</p>
            <p className="text-sm text-stone-600">{order.address.city} — {order.address.pincode}, {order.address.state}</p>
            <p className="text-sm font-mono text-stone-500 mt-1">{order.address.phone}</p>
          </div>
          <div className="border border-stone-200 bg-white p-6">
            <h2 className="font-heading font-semibold">Payment</h2>
            <p className="text-sm mt-3 font-mono uppercase">{order.payment_method}</p>
            <p className={`text-sm mt-1 font-medium ${order.payment_status === "paid" ? "text-emerald-700" : "text-amber-700"}`} data-testid="payment-status">
              {order.payment_status === "paid" ? "Paid" : order.payment_status === "cod_pending" ? "Pay on delivery" : order.payment_status}
            </p>
            {order.refund_note && <p className="text-sm text-stone-500 mt-2">Refund note: {order.refund_note}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
